/**
 * /performance/reviews + /me/performance/reviews — the manager review queue
 * (ADR-0013 Phase 4). Dual-mounted (overtime-queue pattern): the admin layout
 * for HR who are also managers, and the employee layout for a non-HR employee
 * who is someone's resolved reviewer. The queue lists the caller's manager
 * assignments; opening one shows the subject's self-assessment, the manager
 * rates the same items, submits, and finalizes (which freezes the score).
 *
 * The visibility gate is the SUBJECT's concern, not the manager's — a manager
 * is *entitled* to see their report's self-assessment (D-5). This screen reads
 * the reviewer-or-HR review-context endpoint.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReviewResponseRatings } from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import { meLayoutRoute } from './meLayout.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  finalizeReview,
  getReviewContext,
  listMyAssignments,
  submitManagerReview,
  type AssignmentListItem,
  type ReviewContext,
} from '../api/performance.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

export const performanceReviewQueueRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/performance/reviews',
  component: ReviewQueuePage,
});
export const mePerformanceReviewQueueRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/performance/reviews',
  component: ReviewQueuePage,
});

function ReviewQueuePage(): JSX.Element {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['perf', 'my-assignments'], queryFn: () => listMyAssignments() });
  const [open, setOpen] = useState<AssignmentListItem | null>(null);

  // The manager queue = manager-type assignments where I'm the reviewer and the
  // cycle is in review. (A subject's own manager-assignment also comes back from
  // this endpoint, so filter to reviewer === me.)
  const queue = (q.data?.assignments ?? []).filter(
    (a) => a.type === 'manager' && a.reviewerId === user?.id && a.cycle.status === 'review',
  );

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Reviews to complete</h1>
        <p className="text-sm text-gray-400">Reports whose appraisal is routed to you. Open one to see their self-assessment and write your review.</p>
      </header>

      {q.isLoading ? <Placeholder>Loading…</Placeholder> : q.isError ? <Placeholder error>Could not load your queue.</Placeholder> : queue.length === 0 ? (
        <Placeholder>No reviews are awaiting you right now.</Placeholder>
      ) : (
        <div className="space-y-3">
          {queue.map((a) => (
            <div key={a.id} className="rounded border border-gray-900 p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white">{a.cycle.name}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Subject: <span className="font-mono">{a.subjectEmployeeId}</span> · {a.response?.submittedAt ? 'your draft saved' : 'not started'}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(a)} className={secondaryBtn}>Open review</button>
            </div>
          ))}
        </div>
      )}

      {open ? <ReviewModal assignment={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function ReviewModal({ assignment, onClose }: { assignment: AssignmentListItem; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const ctxQ = useQuery({
    queryKey: ['perf', 'review-context', assignment.cycleId, assignment.subjectEmployeeId],
    queryFn: () => getReviewContext(assignment.cycleId, assignment.subjectEmployeeId),
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center overflow-auto p-6 z-50">
      <div className="bg-gray-950 border border-gray-800 rounded max-w-2xl w-full p-6 space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="font-serif text-xl">Manager review</h2>
          <button type="button" onClick={onClose} className={secondaryBtn} aria-label="Close">Close</button>
        </header>
        {ctxQ.isLoading ? <Placeholder>Loading…</Placeholder> : ctxQ.isError || !ctxQ.data ? <Placeholder error>Could not load the review.</Placeholder> : (
          <ReviewBody ctx={ctxQ.data} cycleId={assignment.cycleId} subjectEmployeeId={assignment.subjectEmployeeId} onDone={() => { void qc.invalidateQueries({ queryKey: ['perf', 'my-assignments'] }); onClose(); }} />
        )}
      </div>
    </div>
  );
}

function ReviewBody({ ctx, cycleId, subjectEmployeeId, onDone }: { ctx: ReviewContext; cycleId: string; subjectEmployeeId: string; onDone: () => void }): JSX.Element {
  const template = ctx.template;
  const selfRatings = ctx.selfAssessment.ratings;
  const initialManager = ctx.managerDraft.ratings;
  const [scores, setScores] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const r of initialManager?.kraRatings ?? []) init[`kra:${r.ref}`] = String(r.score);
    for (const r of initialManager?.competencyRatings ?? []) init[`comp:${r.ref}`] = String(r.score);
    return init;
  });
  const [recommendation, setRecommendation] = useState('');
  const [comments, setComments] = useState(initialManager?.comments ?? '');

  const submitMut = useMutation({
    mutationFn: () => {
      const ratings: ReviewResponseRatings = {
        kraRatings: ctx.kras.map((k) => ({ ref: k.id, score: Number(scores[`kra:${k.id}`] ?? 0) })),
        competencyRatings: (template?.competencies ?? []).map((c) => ({ ref: c.key, score: Number(scores[`comp:${c.key}`] ?? 0) })),
        ...(comments ? { comments } : {}),
      };
      return submitManagerReview(ctx.assignmentIds.manager, ratings);
    },
  });
  const finalizeMut = useMutation({
    mutationFn: () => finalizeReview(cycleId, subjectEmployeeId, recommendation || undefined),
    onSuccess: onDone,
  });

  const selfScore = (kind: 'kra' | 'comp', ref: string): number | undefined => {
    const list = kind === 'kra' ? selfRatings?.kraRatings : selfRatings?.competencyRatings;
    return list?.find((r) => r.ref === ref)?.score;
  };
  const min = template?.ratingScale.min ?? 1;
  const max = template?.ratingScale.max ?? 5;
  const set = (key: string, v: string): void => setScores((s) => ({ ...s, [key]: v }));

  return (
    <div className="space-y-4">
      {ctx.subject ? <p className="text-sm text-white">{ctx.subject.firstName} {ctx.subject.lastName} <span className="text-gray-500 font-mono text-xs ml-1">{ctx.subject.employeeNumber}</span></p> : null}
      {!ctx.selfAssessment.submittedAt ? <Placeholder>Waiting on the employee's self-assessment before you can review.</Placeholder> : (
        <>
          <RatingSection title="KRAs" rows={ctx.kras.map((k) => ({ ref: k.id, label: k.title, sub: k.weight != null ? `${Number(k.weight)}%` : undefined }))}
            keyFor={(ref) => `kra:${ref}`} self={(ref) => selfScore('kra', ref)} scores={scores} min={min} max={max} onChange={set} />
          {template ? (
            <RatingSection title="Competencies" rows={template.competencies.map((c) => ({ ref: c.key, label: c.label }))}
              keyFor={(ref) => `comp:${ref}`} self={(ref) => selfScore('comp', ref)} scores={scores} min={min} max={max} onChange={set} />
          ) : null}

          <label className="block space-y-1">
            <span className="text-xs text-gray-400">Appraisal comments</span>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className={inputCls} />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-gray-400">Recommendation (recorded on the outcome; never a pay change)</span>
            <input value={recommendation} onChange={(e) => setRecommendation(e.target.value)} className={inputCls} placeholder="e.g. Promote to senior" />
          </label>

          <div className="flex gap-2">
            <button type="button" onClick={() => submitMut.mutate()} disabled={submitMut.isPending} className={secondaryBtn}>{submitMut.isPending ? 'Saving…' : ctx.managerDraft.submittedAt ? 'Update review' : 'Save review'}</button>
            <button type="button" onClick={() => finalizeMut.mutate()} disabled={finalizeMut.isPending || !ctx.managerDraft.submittedAt} className={primaryBtn} title={ctx.managerDraft.submittedAt ? '' : 'Save your review first'}>
              {finalizeMut.isPending ? 'Finalizing…' : 'Finalize'}
            </button>
          </div>
          {!ctx.managerDraft.submittedAt ? <p className="text-xs text-gray-500">Save your review, then finalize to freeze the score.</p> : null}
          <ApiErrorNotice error={submitMut.error ?? finalizeMut.error} fallback="Could not save review." compact />
        </>
      )}
    </div>
  );
}

function RatingSection({ title, rows, keyFor, self, scores, min, max, onChange }: {
  title: string;
  rows: Array<{ ref: string; label: string; sub?: string }>;
  keyFor: (ref: string) => string;
  self: (ref: string) => number | undefined;
  scores: Record<string, string>;
  min: number;
  max: number;
  onChange: (key: string, v: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
      {rows.map((r) => {
        const k = keyFor(r.ref);
        const s = self(r.ref);
        return (
          <div key={r.ref} className="flex items-center justify-between gap-3 rounded border border-gray-900 p-2">
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{r.label}{r.sub ? <span className="text-gray-500 text-xs ml-2">{r.sub}</span> : null}</p>
              <p className="text-xs text-gray-500">self: {s ?? '—'}</p>
            </div>
            <input type="number" min={min} max={max} value={scores[k] ?? ''} onChange={(e) => onChange(k, e.target.value)} aria-label={`${r.label} rating`} className={`${inputCls} w-20`} />
          </div>
        );
      })}
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>{children}</div>;
}

const inputCls = 'rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';
const primaryBtn = 'rounded bg-green-700 text-white text-xs px-3 py-1.5 disabled:opacity-50';
const secondaryBtn = 'rounded border border-gray-700 text-xs px-3 py-1.5 hover:bg-gray-900';
